import React, { useEffect, useMemo } from 'react';
import { Search, X, CheckCircle, Loader2 } from 'lucide-react';
import './SearchSelectModal.css';

export interface SearchSelectItem {
  id: number | string;
  name: string;
  detail?: string;
}

interface SearchSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  items: SearchSelectItem[];
  selectedId?: string | number | null;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSelect: (item: SearchSelectItem) => void;
  onClear?: () => void;
  clearLabel?: string;
  searchPlaceholder?: string;
  loading?: boolean;
  emptyLabel?: string;
  emptySearchLabel?: string;
}

const SearchSelectModal: React.FC<SearchSelectModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  items,
  selectedId,
  searchTerm,
  onSearchChange,
  onSelect,
  onClear,
  clearLabel = 'Limpar seleção',
  searchPlaceholder = 'Buscar...',
  loading = false,
  emptyLabel = 'Nenhum item disponível',
  emptySearchLabel = 'Nenhum item encontrado',
}) => {
  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => {
      const name = String(item.name || '').toLowerCase();
      const detail = String(item.detail || '').toLowerCase();
      return name.includes(term) || detail.includes(term);
    });
  }, [items, searchTerm]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const hasSelection =
    selectedId !== null &&
    selectedId !== undefined &&
    String(selectedId) !== '';

  return (
    <div className="search-select-modal-overlay" onClick={onClose}>
      <div
        className="search-select-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-select-modal-title"
      >
        <div className="search-select-modal-header">
          <div className="search-select-modal-header-left">
            <div className="search-select-modal-icon">{icon}</div>
            <div>
              <h3 id="search-select-modal-title" className="search-select-modal-title">
                {title}
              </h3>
              {subtitle && (
                <p className="search-select-modal-subtitle">{subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="search-select-modal-close"
            aria-label="Fechar"
          >
            <X size={24} />
          </button>
        </div>

        <div className="search-select-modal-body">
          <div className="search-select-modal-search-box">
            <Search size={18} className="search-select-modal-search-icon" />
            <input
              type="text"
              className="search-select-modal-search-input"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
            />
            {loading && (
              <div className="search-select-modal-search-loading">
                <Loader2 size={16} className="search-select-modal-spinning" />
              </div>
            )}
          </div>

          <div className="search-select-modal-list">
            {loading ? (
              <div className="search-select-modal-empty">
                <Loader2 size={32} className="search-select-modal-spinning" />
                <p>Carregando...</p>
              </div>
            ) : filteredItems.length > 0 ? (
              filteredItems.map((item) => {
                const selected = String(selectedId) === String(item.id);
                return (
                  <button
                    type="button"
                    key={item.id}
                    className={`search-select-modal-item${selected ? ' selected' : ''}`}
                    onClick={() => onSelect(item)}
                  >
                    <div className="search-select-modal-item-avatar">{icon}</div>
                    <div className="search-select-modal-item-info">
                      <div className="search-select-modal-item-name">{item.name}</div>
                      {item.detail && (
                        <div className="search-select-modal-item-detail">{item.detail}</div>
                      )}
                    </div>
                    {selected && (
                      <CheckCircle size={20} className="search-select-modal-item-check" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="search-select-modal-empty">
                <div className="search-select-modal-empty-icon">{icon}</div>
                <p>{searchTerm.trim() ? emptySearchLabel : emptyLabel}</p>
              </div>
            )}
          </div>
        </div>

        {hasSelection && onClear && (
          <div className="search-select-modal-footer">
            <button
              type="button"
              className="search-select-modal-btn-secondary"
              onClick={onClear}
            >
              <X size={18} />
              {clearLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchSelectModal;
